/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tabs/BasicTab.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Basic tab component for Conference form - General Information
────────────────────────────────────────────────────────────────*/

import { Users } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Toggle } from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import { LanguageSelector } from '../../extensions/components/LanguageSelector';
import SecretField from '../../../components/ui/SecretField';
import type { ConferenceRoom } from '../types';

interface BasicTabProps {
  formData: ConferenceRoom;
  errors: Record<string, string>;
  isEditMode: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conferenceProfiles: any[];
  loadingProfiles: boolean;
  mohClasses: string[];
  isLoadingMoh: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (field: keyof ConferenceRoom, value: any) => void;
  onRegeneratePin: (field: 'moderatorPin' | 'participantPin') => void;
}

export function BasicTab({
  formData,
  errors,
  isEditMode,
  conferenceProfiles,
  loadingProfiles,
  mohClasses,
  isLoadingMoh,
  onChange,
  onRegeneratePin,
}: BasicTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  const buildTooltipContent = (tooltipKey: string, debugKey?: string) => {
    const base = t(tooltipKey);
    if (!debugKey || !isDebugModeActive) {
      return base;
    }
    return `${base}\n\n${t('conferences:debug.field')}: ${t(debugKey)}`;
  };

  return (
    <div className="space-y-3">
      <FormBox
        title={t('conferences:sections.basic')}
        icon={<Users className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          {/* Row 1 */}
          <SimpleTooltip
            content={t('conferences:tooltips.extension')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.extension')} *
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.extension}
            onChange={(e) => onChange('extension', e.target.value)}
            className={`px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${errors.extension ? 'border-red-500' : 'border-border'
              }`}
            placeholder={t('conferences:fields.extensionPlaceholder')}
            disabled={isEditMode}
          />
          <SimpleTooltip
            content={t('conferences:tooltips.name')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.name')} *
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            className={`px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${errors.name ? 'border-red-500' : 'border-border'
              }`}
            placeholder={t('conferences:fields.namePlaceholder')}
          />

          {/* Row 2 */}
          <SimpleTooltip
            content={t('conferences:tooltips.description')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.description')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => onChange('description', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder={t('conferences:fields.descriptionPlaceholder')}
          />
          <SimpleTooltip
            content={t('conferences:tooltips.language')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.language')}
            </label>
          </SimpleTooltip>
          <LanguageSelector
            value={formData.language}
            onChange={(value) => onChange('language', value)}
            onSoundPrefixChange={(fullPath) => onChange('soundPrefix', fullPath)}
            showVoiceSelector={true}
            showLabel={false}
          />

          {/* Row 3 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.maxMembers', 'conferences:debug.max_members')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.maxMembers')}
            </label>
          </SimpleTooltip>
          <input
            type="number"
            value={formData.maxMembers}
            onChange={(e) => onChange('maxMembers', parseInt(e.target.value) || 0)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="0 (unlimited)"
            min="0"
          />
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.mohSound', 'conferences:debug.moh_sound')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.mohSound')}
            </label>
          </SimpleTooltip>
          <select
            value={formData.mohSound || 'default'}
            onChange={(e) => onChange('mohSound', e.target.value)}
            disabled={isLoadingMoh}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingMoh ? (
              <option value="default">{t('common.loading') || 'Loading...'}</option>
            ) : (
              mohClasses.map((className) => (
                <option key={className} value={className}>
                  {className === 'default' ? t('common.default') || 'Default' : className}
                </option>
              ))
            )}
          </select>

          {/* Row 4 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.muteOnEntry', 'conferences:debug.mute_member_on_join')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.muteOnEntry')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.muteOnEntry}
              onChange={(value) => onChange('muteOnEntry', value)}
            />
          </div>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.deafOnEntry', 'conferences:debug.deaf_member_on_join')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.deafOnEntry')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.deafOnEntry}
              onChange={(value) => onChange('deafOnEntry', value)}
            />
          </div>

          {/* Row 5 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.skipEmptyConference', 'conferences:debug.skip_empty')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.skipEmptyConference')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.skipEmptyConference}
              onChange={(value) => onChange('skipEmptyConference', value)}
            />
          </div>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.lockConference')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.lockConference')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.lockConference}
              onChange={(value) => onChange('lockConference', value)}
            />
          </div>

          {/* Row 6 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.endConfOnModExit', 'conferences:debug.endconf_grace_time')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.endConfOnModExit')}
            </label>
          </SimpleTooltip>
          <input
            type="number"
            value={formData.endConfOnModExit}
            onChange={(e) => onChange('endConfOnModExit', parseInt(e.target.value) || 0)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="0"
            min="0"
          />
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.waitForModerator', 'conferences:debug.wait_mod')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.waitForModerator')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.waitForModerator}
              onChange={(value) => onChange('waitForModerator', value)}
            />
          </div>

          {/* Row 7 */}
          <SimpleTooltip
            content={t('conferences:tooltips.moderatorPin')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.moderatorPin')}
            </label>
          </SimpleTooltip>
          <SecretField
            type="input"
            value={formData.moderatorPin}
            onChange={(value) => onChange('moderatorPin', value)}
            placeholder={t('conferences:fields.moderatorPinPlaceholder')}
            showToggle={true}
            showCopy={true}
            showGenerate={true}
            onGenerate={() => onRegeneratePin('moderatorPin')}
          />
          <SimpleTooltip
            content={t('conferences:tooltips.participantPin')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.participantPin')}
            </label>
          </SimpleTooltip>
          <SecretField
            type="input"
            value={formData.participantPin}
            onChange={(value) => onChange('participantPin', value)}
            placeholder={t('conferences:fields.participantPinPlaceholder')}
            showToggle={true}
            showCopy={true}
            showGenerate={true}
            onGenerate={() => onRegeneratePin('participantPin')}
          />
          {/* Row 8 */}
          <SimpleTooltip
            content={t('conferences:tooltips.timezone')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.timezone')}
            </label>
          </SimpleTooltip>
          <TimezoneSelect
            value={formData.timezone}
            onChange={(value) => onChange('timezone', value)}
            placeholder="UTC"
          />
          <SimpleTooltip
            content={t('conferences:tooltips.profile')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.profile')}
            </label>
          </SimpleTooltip>
          <select
            value={formData.profile_id || 1}
            onChange={(e) => onChange('profile_id', e.target.value)}
            className="px-3 py-2 text-sm border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent border-border"
            disabled={loadingProfiles}
          >
            {loadingProfiles ? (
              <option value="">Loading profiles...</option>
            ) : (
              conferenceProfiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name} {profile.is_system ? '(System)' : ''}
                </option>
              ))
            )}
          </select>

          {/* Row 9 - Enabled at the end */}
          <SimpleTooltip
            content={t('conferences:tooltips.enabled')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.enabled')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.enabled}
              onChange={(value) => onChange('enabled', value)}
            />
          </div>
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">
              {conferenceProfiles.find((p) => String(p.id) === String(formData.profile_id))?.description ||
                'Select a profile to inherit audio/video settings'}
            </p>
          </div>
        </div>
      </FormBox>
    </div>
  );
}

